import { Controller } from '@hotwired/stimulus';
import * as Sentry from '@sentry/browser';
import * as DateFns from 'date-fns';
import { compact, isEmpty, isEqual } from 'es-toolkit/compat';
import querystring from 'query-string';

import RailsApi from '../models/rails-api';

const DATE_FORMAT = 'yyyy-MM-dd';
const EARLIEST_DATE = new Date(2017, 0, 1);
const EARLIEST_DATE_STRING = DateFns.format(EARLIEST_DATE, DATE_FORMAT);
const CUSTOM_DATE_RANGE = 'custom';

interface ApiResponse {
  cursor: string;
  containerHTML: string;
  paginatorHTML: string;
}

export default class extends Controller<HTMLElement> {
  static targets = [
    'accountCheckbox',
    'accountsInput',
    'accountsToggleInput',
    'accountsLabel',
    'activityList',
    'csvDownloadFlash',
    'csvDownloadLink',
    'datePickerInputs',
    'dateRangeErrorMessage',
    'dateRangeFlashError',
    'dateRangeInput',
    'endDateInput',
    'footer',
    'invalidDateRangeError',
    'invalidEndDateError',
    'invalidStartDateError',
    'loader',
    'noActivityFoundError',
    'startDateInput',
    'transactionTypeCheckbox',
    'transactionTypesInput',
    'transactionTypesLabel',
    'transactionTypesToggleInput',
  ];

  accountCheckboxTargets: HTMLInputElement[];

  accountsInputTarget: HTMLElement;

  accountsLabelTarget: HTMLElement;

  accountsToggleInputTarget: HTMLInputElement;

  activityListTarget: HTMLTableElement;

  apiUri: string;

  csvApiUri: string;

  csvDownloadLinkTarget: HTMLButtonElement;

  csvDownloadFlashTarget: HTMLElement;

  cursor: string | null;

  datePickerInputsTarget: HTMLElement;

  dateRangeErrorMessageTarget: HTMLElement;

  dateRangeFlashErrorTarget: HTMLElement;

  dateRangeInputTarget: HTMLSelectElement;

  defaultTransactionTypesLabel: string;

  defaultAccountsLabel: string;

  endDateInputTarget: HTMLInputElement;

  footerTarget: HTMLElement;

  invalidDateRangeErrorTarget: HTMLElement;

  invalidEndDateErrorTarget: HTMLInputElement;

  invalidStartDateErrorTarget: HTMLInputElement;

  isFiltering: boolean;

  isLoadingNextPage: boolean;

  loaderTarget: HTMLTableElement;

  noActivityFoundErrorTarget: HTMLElement;

  previousAccounts: Array<string>;

  previousDateRange: string;

  previousEndDate: string;

  previousStartDate: string;

  previousTransactionTypes: Array<string>;

  startDateInputTarget: HTMLInputElement;

  transactionTypeCheckboxTargets: HTMLInputElement[];

  transactionTypesInputTarget: HTMLElement;

  transactionTypesLabelTarget: HTMLElement;

  transactionTypesToggleInputTarget: HTMLInputElement;

  initialize() {
    if (typeof this.element.dataset.apiUri !== 'string' || isEmpty(this.element.dataset.apiUri)) {
      throw new Error('data-api-uri must be provided');
    }

    if (typeof this.element.dataset.csvApiUri !== 'string' || isEmpty(this.element.dataset.csvApiUri)) {
      throw new Error('data-csv-api-uri must be provided');
    }

    this.isFiltering = false;
    this.isLoadingNextPage = false;
    this.apiUri = this.element.dataset.apiUri;
    this.csvApiUri = this.element.dataset.csvApiUri;
    this.cursor = this.element.dataset.initialCursor || '';
    this.defaultTransactionTypesLabel = this.transactionTypesLabelTarget.textContent || 'Activity transactions';
    this.defaultAccountsLabel = this.accountsLabelTarget.textContent || 'Accounts';

    this.setPreviousValues();
  }

  connect() {
    this.toggleDateRangeErrorMessageVisibility();
    this.toggleDateErrorVisbilities();
    this.toggleDatePickersVisbility();
    this.toggleFooterVisibility();
    this.updateTransactionTypesLabel();
    this.updateCsvLink();
  }

  setPreviousValues() {
    this.previousDateRange = this.dateRange;
    this.previousStartDate = this.startDate;
    this.previousEndDate = this.endDate;
    this.previousAccounts = this.accounts;
    this.previousTransactionTypes = this.transactionTypes;
  }

  get dateRange(): string {
    return this.dateRangeInputTarget.value;
  }

  get startDate(): string {
    return this.startDateInputTarget.value;
  }

  set startDate(value: string) {
    this.startDateInputTarget.value = value;
  }

  get endDate(): string {
    return this.endDateInputTarget.value;
  }

  set endDate(value: string) {
    this.endDateInputTarget.value = value;
  }

  get isCustomDateRange(): boolean {
    return this.dateRange === CUSTOM_DATE_RANGE;
  }

  get isStartDateValid(): boolean {
    const parsedStartDate = DateFns.parse(this.startDate, DATE_FORMAT, new Date());
    return DateFns.isValid(parsedStartDate);
  }

  get isEndDateValid(): boolean {
    const parsedEndDate = DateFns.parse(this.endDate, DATE_FORMAT, new Date());
    return DateFns.isValid(parsedEndDate);
  }

  get isDateRangeValid(): boolean {
    const parsedStartDate = DateFns.parse(this.startDate, DATE_FORMAT, new Date());
    const parsedEndDate = DateFns.parse(this.endDate, DATE_FORMAT, new Date());
    return DateFns.isBefore(parsedStartDate, parsedEndDate);
  }

  get hasDateRangeErrors(): boolean {
    return this.isCustomDateRange && (!this.isStartDateValid || !this.isEndDateValid || !this.isDateRangeValid);
  }

  get isDateRangeBefore2017(): boolean {
    const parsedStartDate = DateFns.parse(this.startDate, DATE_FORMAT, new Date());
    return this.isCustomDateRange && DateFns.isBefore(parsedStartDate, DateFns.add(EARLIEST_DATE, { seconds: 1 }));
  }

  get accounts(): Array<string> {
    return compact(
      this.accountCheckboxTargets.map(target => {
        if (target.checked) {
          return target.value;
        }
        return null;
      })
    );
  }

  get transactionTypes(): Array<string> {
    return compact(
      this.transactionTypeCheckboxTargets.map(target => {
        if (target.checked) {
          return target.value;
        }
        return null;
      })
    );
  }

  get filterQueryParams() {
    return querystring.stringify(
      {
        'filters[date_range]': this.dateRange,
        'filters[start_date_inclusive]': this.startDate,
        'filters[end_date_inclusive]': this.endDate,
        'filters[selected_sub_account_ids]': [''].concat(this.accounts),
        'filters[transaction_type_groups]': [''].concat(this.transactionTypes),
      },
      { arrayFormat: 'bracket' }
    );
  }

  get dateRangeChanged() {
    // If this is a custom date range, wait on the specific start / end date to update for a change
    if (this.isCustomDateRange) return false;
    return this.dateRange !== this.previousDateRange;
  }

  get startDateChanged() {
    return this.startDate !== this.previousStartDate;
  }

  get endDateChanged() {
    return this.endDate !== this.previousEndDate;
  }

  get accountsChanged() {
    return !isEqual(this.accounts, this.previousAccounts);
  }

  get transactionTypesChanged() {
    return !isEqual(this.transactionTypes, this.previousTransactionTypes);
  }

  get hasChanges() {
    return (
      this.dateRangeChanged ||
      this.startDateChanged ||
      this.endDateChanged ||
      this.accountsChanged ||
      this.transactionTypesChanged
    );
  }

  onDocumentClick(event: Event) {
    // Only execute the filtering if we're not clicking inside one of the dropdown menus for accounts or activity types
    const target = event.target as HTMLElement;
    const accountsDropdownElm = this.accountsInputTarget.querySelector('.DropdownInput-options') as HTMLElement;
    const transactionTypesDropdownElm = this.transactionTypesInputTarget.querySelector(
      '.DropdownInput-options'
    ) as HTMLElement;
    if (
      !accountsDropdownElm.contains(target) &&
      !transactionTypesDropdownElm.contains(target) &&
      !this.startDateInputTarget.contains(target) &&
      !this.endDateInputTarget.contains(target)
    ) {
      void this.filter();
    }
  }

  onChangeDateRange(event: Event): void {
    event.stopPropagation();

    this.toggleDatePickersVisbility();

    if (!this.isCustomDateRange) {
      void this.filter();
    }
  }

  onChangeStartDate(event: Event): void {
    event.stopPropagation();

    const parsedStartDate = DateFns.parse(this.startDate, DATE_FORMAT, new Date());

    // Check if this date is before 1/1/2017 and update accordingly
    if (DateFns.isAfter(EARLIEST_DATE, parsedStartDate)) {
      this.startDate = EARLIEST_DATE_STRING;
    }

    this.toggleDateErrorVisbilities();

    // Only filter if we're searching a custom date range
    if (this.isCustomDateRange) {
      void this.filter();
    }
  }

  onChangeEndDate(event: Event): void {
    event.stopPropagation();

    const parsedEndDate = DateFns.parse(this.endDate, DATE_FORMAT, new Date());

    // Check if this date is before 1/1/2017 and update accordingly
    if (DateFns.isAfter(EARLIEST_DATE, parsedEndDate)) {
      this.endDate = EARLIEST_DATE_STRING;
    }

    this.toggleDateErrorVisbilities();

    // Only filter if we're searching a custom date range
    if (this.isCustomDateRange) {
      void this.filter();
    }
  }

  blurOnEnter(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
    }
  }

  showLoader() {
    this.loaderTarget.classList.remove('u-displayNone');
  }

  hideLoader() {
    this.loaderTarget.classList.add('u-displayNone');
  }

  toggleDatePickersVisbility(): void {
    this.datePickerInputsTarget.classList.toggle('u-displayNone', !this.isCustomDateRange);
  }

  toggleAllTransactionTypes() {
    if (this.transactionTypesToggleInputTarget.checked) {
      this.checkAllTransactionTypes();
    } else {
      this.uncheckAllTransactionTypes();
    }
  }

  toggleAllAccounts() {
    if (this.accountsToggleInputTarget.checked) {
      this.checkAllAccounts();
    } else {
      this.uncheckAllAccounts();
    }
  }

  updateTransactionTypesLabel() {
    const { transactionTypes } = this;

    if (transactionTypes.length === 0) {
      this.transactionTypesLabelTarget.textContent = `Add ${this.defaultTransactionTypesLabel}`;
    } else if (transactionTypes.length > 1) {
      this.transactionTypesLabelTarget.textContent = `${transactionTypes.length} ${this.defaultTransactionTypesLabel}`;
    } else {
      const checkedTarget = this.transactionTypeCheckboxTargets.find(target => target.checked);
      this.transactionTypesLabelTarget.textContent =
        checkedTarget?.parentElement?.querySelector('label')?.textContent || this.defaultTransactionTypesLabel;
    }
  }

  updateAccountsLabel() {
    const { accounts } = this;

    if (accounts.length === 0) {
      this.accountsLabelTarget.textContent = `Add ${this.defaultAccountsLabel}`;
    } else if (accounts.length > 1) {
      this.accountsLabelTarget.textContent = `${accounts.length} ${this.defaultAccountsLabel}`;
    } else {
      const checkedTarget = this.accountCheckboxTargets.find(target => target.checked);
      this.accountsLabelTarget.textContent =
        checkedTarget?.parentElement?.querySelector('label')?.textContent || this.defaultAccountsLabel;
    }
  }

  toggleDateRangeFlashErrorVisibility() {
    this.dateRangeFlashErrorTarget.classList.toggle('u-displayNone', !this.isDateRangeBefore2017);
  }

  toggleDateRangeErrorMessageVisibility() {
    this.dateRangeErrorMessageTarget.classList.toggle(
      'u-displayNone',
      !!(this.cursor || !this.isDateRangeBefore2017 || this.activityListTarget.childElementCount === 0)
    );
  }

  toggleDateErrorVisbilities(): void {
    this.invalidStartDateErrorTarget.classList.toggle('u-displayNone', true);
    this.invalidEndDateErrorTarget.classList.toggle('u-displayNone', true);
    this.invalidDateRangeErrorTarget.classList.toggle('u-displayNone', true);

    if (!this.isStartDateValid || !this.isEndDateValid) {
      this.invalidStartDateErrorTarget.classList.toggle('u-displayNone', this.isStartDateValid);
      this.invalidEndDateErrorTarget.classList.toggle('u-displayNone', this.isEndDateValid);
    } else if (!this.isDateRangeValid) {
      this.invalidDateRangeErrorTarget.classList.toggle('u-displayNone', false);
    }
  }

  toggleNoActivityFoundErrorVisibility(): void {
    this.noActivityFoundErrorTarget.classList.toggle('u-displayNone', this.activityListTarget.childElementCount > 0);
  }

  checkAllTransactionTypes() {
    this.transactionTypeCheckboxTargets.forEach(checkboxTarget => {
      checkboxTarget.checked = true;
    });

    this.updateTransactionTypesLabel();
  }

  uncheckAllTransactionTypes() {
    this.transactionTypeCheckboxTargets.forEach(checkboxTarget => {
      checkboxTarget.checked = false;
    });

    this.updateTransactionTypesLabel();
  }

  checkAllAccounts() {
    this.accountCheckboxTargets.forEach(checkboxTarget => {
      checkboxTarget.checked = true;
    });

    this.updateAccountsLabel();
  }

  uncheckAllAccounts() {
    this.accountCheckboxTargets.forEach(checkboxTarget => {
      checkboxTarget.checked = false;
    });

    this.updateAccountsLabel();
  }

  onChangeTransactionTypeCheckbox() {
    this.updateTransactionTypesToggle();
    this.updateTransactionTypesLabel();
  }

  onChangeAccountCheckbox() {
    this.updateAccountsToggle();
    this.updateAccountsLabel();
  }

  updateTransactionTypesToggle() {
    const uncheckedOption = this.transactionTypeCheckboxTargets.find(target => !target.checked);
    this.transactionTypesToggleInputTarget.checked = !uncheckedOption;
  }

  updateAccountsToggle() {
    const uncheckedOption = this.accountCheckboxTargets.find(target => !target.checked);
    this.accountsToggleInputTarget.checked = !uncheckedOption;
  }

  selectOnlyTransactionType(event: Event) {
    const category = (event.target as HTMLElement).dataset.value;
    const selectedCheckbox = this.transactionTypeCheckboxTargets.find(({ value }) => value === category);

    this.uncheckAllTransactionTypes();

    if (selectedCheckbox) {
      selectedCheckbox.checked = true;
    }

    this.updateTransactionTypesToggle();
    this.updateTransactionTypesLabel();
  }

  selectOnlyAccount(event: Event) {
    const account = (event.target as HTMLElement).dataset.value;
    const selectedCheckbox = this.accountCheckboxTargets.find(({ value }) => value === account);

    this.uncheckAllAccounts();

    if (selectedCheckbox) {
      selectedCheckbox.checked = true;
    }

    this.updateAccountsToggle();
    this.updateAccountsLabel();
  }

  toggleFooterVisibility() {
    this.footerTarget.classList.toggle('u-displayNone', this.activityListTarget.childElementCount === 0);
  }

  updateCsvLink() {
    this.csvDownloadLinkTarget.setAttribute('href', `${this.csvApiUri}?${this.filterQueryParams}`);
  }

  showCSVDownloadFlash() {
    this.csvDownloadFlashTarget.classList.remove('u-displayNone');
  }

  /**
   * Filters the transactions by the current filters if they've changed
   *
   * @event ActivityTransactionsController#updatePaginatorHTML
   * @type {object}
   * @property {string} paginatorHTML - The updated paginator html
   */
  async filter() {
    if (!this.hasChanges) return;
    if (this.hasDateRangeErrors) return;
    if (this.isFiltering) return;

    try {
      this.isFiltering = true;

      this.showLoader();
      this.setPreviousValues();

      window.history.replaceState({}, document.title, `?${this.filterQueryParams}`);
      const apiResponse = await RailsApi.fetch(`${this.apiUri}?${this.filterQueryParams}`);

      if (apiResponse.ok) {
        const { cursor, containerHTML, paginatorHTML } = (await apiResponse.json()) as ApiResponse;

        this.activityListTarget.innerHTML = containerHTML;
        this.cursor = cursor;
        this.dispatch('updatePaginatorHTML', { detail: { paginatorHTML } });
      } else {
        // TODO: Render an error state as well as clearing the list
        this.activityListTarget.innerHTML = '';
        this.cursor = null;
        this.dispatch('updatePaginatorHTML', { detail: { paginatorHTML: '' } });
      }

      if (this.noActivityFoundErrorTarget) {
        this.toggleNoActivityFoundErrorVisibility();
      }
      this.toggleDateRangeFlashErrorVisibility();
      this.toggleDateRangeErrorMessageVisibility();
      this.toggleDateErrorVisbilities();
      this.toggleFooterVisibility();
      this.updateCsvLink();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.isFiltering = false;
      this.hideLoader();
    }
  }
}
